/**
 * The composer saving itself.
 *
 * A second after the writer stops typing, the whole letter goes to the server
 * as a draft - created on the first save, overwritten on every one after. So a
 * letter left half-written is never lost: close the window and it is under
 * Drafts; open another page and it is saved on the way out; close the browser
 * and the last save leaves with the tab (`saveDraftOnExit`).
 *
 * Nothing is created for an empty composer. Opening "New message" and closing
 * it again must not leave an empty draft behind, so the first save waits until
 * there is something in it: an address, a subject, a word or a file. Once a
 * draft exists it is saved whatever it holds - emptying it is an edit too.
 *
 * ONE SAVE AT A TIME. A change made while a save is in flight is saved when that
 * one lands, never beside it: two creates racing would leave two drafts of one
 * letter, and two updates racing could land in the wrong order.
 *
 * The caller says when to stop. Sending turns saving off (`paused`) so a save
 * cannot land after the letter it describes is gone; a finished send calls
 * `stop()`, and a discarded draft calls `discard()`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createDraft, deleteDraft, saveDraft, saveDraftOnExit, type DraftContent } from "../../../../lib/mailDrafts";

/** How long the writer has to pause before a save goes out. */
export const SAVE_DELAY_MS = 1000;

export type SaveState = "idle" | "saving" | "saved" | "error";

export interface DraftAutosave {
  state: SaveState;
  /** When the last save landed, for "Saved 14:03". */
  savedAt: Date | null;
  /** The server's id - the draft being continued, or the one the first save made. */
  draftId: () => string | null;
  /** Save whatever is unsaved and wait for it. Answers the draft's id, or null
   *  when there is nothing to save and no draft yet. */
  flush: () => Promise<string | null>;
  /** Stop saving for good: the letter has gone, and its draft with it. */
  stop: () => void;
  /** Stop saving and delete the draft on the server. */
  discard: () => Promise<void>;
}

export function useDraftAutosave({
  content,
  hasContent,
  initialId,
  paused,
  onSaved,
}: {
  content: DraftContent;
  /** Whether there is anything worth creating a draft for. */
  hasContent: boolean;
  /** The draft being continued, when the composer was opened from Drafts. */
  initialId: string | null;
  /** True while the letter is being sent. */
  paused: boolean;
  /** After every save that landed - the rail's Drafts count moves with it. Also
   *  called for the save a closing window makes, after the window is gone. */
  onSaved?: () => void;
}): DraftAutosave {
  const [state, setState] = useState<SaveState>(initialId ? "saved" : "idle");
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const serialized = JSON.stringify(content);
  const id = useRef<string | null>(initialId);
  const latest = useRef({ content, serialized, hasContent });
  latest.current = { content, serialized, hasContent };
  const savedHook = useRef(onSaved);
  savedHook.current = onSaved;
  // A continued draft starts out saved: what the composer shows is what the
  // server holds, and saving it straight back would be a write for nothing.
  const lastSaved = useRef<string | null>(initialId ? serialized : null);
  const inFlight = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  const stopped = useRef(false);
  const timer = useRef<number | null>(null);

  const dirty = () => {
    const now = latest.current;
    return now.serialized !== lastSaved.current && (id.current !== null || now.hasContent);
  };

  const runSave = useCallback((): Promise<void> => {
    if (inFlight.current) {
      again.current = true;
      return inFlight.current;
    }
    const { content: snapshot, serialized: body } = latest.current;
    setState("saving");
    const run = (async () => {
      try {
        const res = id.current ? await saveDraft(id.current, snapshot) : await createDraft(snapshot);
        id.current = res.id;
        lastSaved.current = body;
        setState("saved");
        setSavedAt(new Date());
        savedHook.current?.();
      } catch {
        setState("error");
      } finally {
        inFlight.current = null;
        if (again.current && !stopped.current) {
          again.current = false;
          if (dirty()) void runSave();
        }
      }
    })();
    inFlight.current = run;
    return run;
  }, []);

  // The debounce. Re-armed on every change, so a save goes out a second after
  // the LAST keystroke rather than once a second while typing.
  useEffect(() => {
    if (paused || stopped.current || !dirty()) return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      void runSave();
    }, SAVE_DELAY_MS);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    };
    // `serialized` stands for the whole content; `dirty` reads the refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, hasContent, paused, runSave]);

  // Leaving the page. An ordinary request would be cancelled with the tab, so
  // the last save goes as a keepalive one. A draft whose FIRST save is still in
  // flight is left to that save: a second create here would be a second draft.
  useEffect(() => {
    const onExit = () => {
      if (stopped.current || !dirty()) return;
      if (!id.current && inFlight.current) return;
      saveDraftOnExit(id.current, latest.current.content);
    };
    window.addEventListener("pagehide", onExit);
    window.addEventListener("beforeunload", onExit);
    return () => {
      window.removeEventListener("pagehide", onExit);
      window.removeEventListener("beforeunload", onExit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The composer closing - its own close button, another page in the app. The
  // page is still alive, so an ordinary save finishes after the window is gone.
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      if (stopped.current || !dirty()) return;
      void runSave();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const flush = useCallback(async () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (inFlight.current) await inFlight.current;
    if (dirty()) await runSave();
    if (dirty()) throw new Error("draft_not_saved");
    return id.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runSave]);

  const stop = useCallback(() => {
    stopped.current = true;
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const discard = useCallback(async () => {
    stop();
    if (inFlight.current) await inFlight.current.catch(() => undefined);
    if (id.current) await deleteDraft(id.current);
    id.current = null;
  }, [stop]);

  return { state, savedAt, draftId: () => id.current, flush, stop, discard };
}
